/**
 * ClinicInfoCards — identity (editable clinic fields, own save), team &
 * permissions (read-only) and system record (read-only). Ported from the
 * former "פרטי קליניקה" tab; plan + limits now live in the manage tab.
 */

import { useEffect, useState } from "react";
import { Buildings, Check, Copy, FloppyDisk, LockSimple, PencilSimple, UsersThree } from "@phosphor-icons/react";
import HoverTip from "../../../../../components/ui/HoverTip";
import { useToast } from "../../../../../contexts/ToastContext";
import { useUpdateClinicFieldMutation } from "../../../../../store/services/nivApi";
import { formatCurrency, formatDate, formatDateTime } from "../clinicDetailConstants";

function ReadRow({ label, children, mono = false }) {
  return (
    <div className="ncd2-read-row">
      <span className="ncd2-read-label">{label}</span>
      <span className={`ncd2-read-value${mono ? " is-mono" : ""}`}>{children ?? "—"}</span>
    </div>
  );
}

const yesNo = (v) => (v ? "כן" : "לא");
// Multi-clinic v2 booking guards: undefined/null → the app only warns
const guardLabel = (v) => (v === true ? "כן" : v === false ? "לא" : "אזהרה בלבד");

const clinicToForm = (clinic) => ({
  name: clinic?.name || "",
  type: clinic?.type || "",
  defaultHourlyRate: clinic?.defaultHourlyRate?.toString() || "",
  defaultMeetingTime: clinic?.defaultMeetingTime?.toString() || "45",
});

export function IdentityCard({ clinic, refetch }) {
  const { showSuccess, showError } = useToast();
  const [updateClinicField, { isLoading: saving }] = useUpdateClinicFieldMutation();
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState(() => clinicToForm(clinic));

  useEffect(() => {
    if (!isEditing) setForm(clinicToForm(clinic));
  }, [clinic, isEditing]);

  const handleSave = async () => {
    try {
      const updates = {};
      if (form.name !== clinic.name) updates.name = form.name;
      if (form.type !== clinic.type) updates.type = form.type;
      if (form.defaultHourlyRate && parseInt(form.defaultHourlyRate, 10) !== clinic.defaultHourlyRate) {
        updates.defaultHourlyRate = parseInt(form.defaultHourlyRate, 10);
      }
      if (parseInt(form.defaultMeetingTime, 10) !== clinic.defaultMeetingTime) {
        updates.defaultMeetingTime = parseInt(form.defaultMeetingTime, 10);
      }
      if (Object.keys(updates).length > 0) {
        await updateClinicField({ clinicId: clinic.clinicId, fieldUpdates: updates }).unwrap();
      }
      setIsEditing(false);
      refetch?.();
      showSuccess("פרטי הקליניקה עודכנו בהצלחה");
    } catch (error) {
      console.error("[CK-9952] Error saving clinic info:", {
        errorCode: "CK-9952",
        errorMessage: error?.message,
        clinicId: clinic.clinicId,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בעדכון פרטי הקליניקה");
    }
  };

  return (
    <section className="ncd2-card">
      <header className="ncd2-card-head">
        <span className="ncd2-card-icon">
          <Buildings size={15} weight="bold" />
        </span>
        <div className="ncd2-card-titles">
          <span className="ncd2-card-eyebrow">ניתן לעריכה</span>
          <h3 className="ncd2-card-title">פרטי הקליניקה</h3>
        </div>
        {!isEditing ? (
          <button type="button" className="ncd2-btn ncd2-btn-secondary ncd2-btn-sm" onClick={() => setIsEditing(true)}>
            <PencilSimple size={14} /> עריכה
          </button>
        ) : (
          <div className="ncd2-edit-actions">
            <button type="button" className="ncd2-btn ncd2-btn-ghost ncd2-btn-sm" onClick={() => setIsEditing(false)} disabled={saving}>
              ביטול
            </button>
            <button type="button" className="ncd2-btn ncd2-btn-primary ncd2-btn-sm" onClick={handleSave} disabled={saving}>
              <FloppyDisk size={14} /> {saving ? "שומר..." : "שמור"}
            </button>
          </div>
        )}
      </header>

      <div className="ncd2-form-grid">
        <label className="ncd2-field">
          <span className="ncd2-field-label">שם הקליניקה</span>
          {isEditing ? (
            <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          ) : (
            <span className="ncd2-field-value">{clinic.name || "—"}</span>
          )}
        </label>
        <label className="ncd2-field">
          <span className="ncd2-field-label">סוג קליניקה</span>
          {isEditing ? (
            <input type="text" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} placeholder="לדוגמה: קלינאית תקשורת" />
          ) : (
            <span className="ncd2-field-value">{clinic.type || "—"}</span>
          )}
        </label>
        <label className="ncd2-field">
          <span className="ncd2-field-label">תעריף שעתי ברירת מחדל</span>
          {isEditing ? (
            <input type="number" inputMode="numeric" dir="ltr" value={form.defaultHourlyRate} onChange={(e) => setForm({ ...form, defaultHourlyRate: e.target.value })} placeholder="0" />
          ) : (
            <span className="ncd2-field-value">{clinic.defaultHourlyRate ? formatCurrency(clinic.defaultHourlyRate) : "—"}</span>
          )}
        </label>
        <label className="ncd2-field">
          <span className="ncd2-field-label">משך פגישה ברירת מחדל (דקות)</span>
          {isEditing ? (
            <input type="number" inputMode="numeric" dir="ltr" value={form.defaultMeetingTime} onChange={(e) => setForm({ ...form, defaultMeetingTime: e.target.value })} />
          ) : (
            <span className="ncd2-field-value">{clinic.defaultMeetingTime} דקות</span>
          )}
        </label>
        <div className="ncd2-field">
          <span className="ncd2-field-label">בעלים</span>
          <span className="ncd2-field-value">{clinic.ownerName || "—"}</span>
        </div>
        <div className="ncd2-field">
          <span className="ncd2-field-label">אימייל</span>
          <span className="ncd2-field-value">{clinic.ownerEmail || "—"}</span>
        </div>
      </div>
    </section>
  );
}

export function TeamCard({ clinic }) {
  const mcSettings = clinic.multiClinicSettings || {};
  const roleCounts = clinic.roleCounts || {};
  const sharesAll = mcSettings.patientSharingMode === "all";
  return (
    <section className="ncd2-card ncd2-readonly">
      <header className="ncd2-card-head">
        <span className="ncd2-card-icon is-muted">
          <UsersThree size={16} weight="fill" />
        </span>
        <div className="ncd2-card-titles">
          <span className="ncd2-card-eyebrow">קריאה בלבד</span>
          <h3 className="ncd2-card-title">צוות והרשאות (v2)</h3>
        </div>
      </header>
      <div className="ncd2-read-grid">
        <ReadRow label="בעלים">{roleCounts.owner ?? 0}</ReadRow>
        <ReadRow label="מנהלים">{roleCounts.manager ?? 0}</ReadRow>
        <ReadRow label="מטפלים">{roleCounts.therapist ?? 0}</ReadRow>
        <ReadRow label="מזכירות">{roleCounts.secretary ?? 0}</ReadRow>
        <ReadRow label="ממתינים לכניסה ראשונה">{clinic.pendingInvitesCount ?? 0}</ReadRow>
        <ReadRow label="מצב שיתוף מטופלים">
          {sharesAll ? (
            <>
              כל המטופלים משותפים
              <span className="ncd2-muted"> (כולל דוחות: {yesNo(mcSettings.allSharedIncludesTreatments === true)})</span>
            </>
          ) : (
            "משויך למטפל/ת"
          )}
        </ReadRow>
        <ReadRow label="חסימת כפל תורים">{guardLabel(mcSettings.preventTherapistDoubleBooking)}</ReadRow>
        <ReadRow label="חסימת תורים בהיעדרות">{guardLabel(mcSettings.blockBookingOnAbsence)}</ReadRow>
        <ReadRow label="גרסת הרשאות" mono>{clinic.permissionsSchemaVersion ?? "legacy"}</ReadRow>
        <ReadRow label="שודרגה למרובת מטפלים">
          {clinic.multiClinicUpgradedAt ? formatDateTime(clinic.multiClinicUpgradedAt) : "—"}
        </ReadRow>
        <ReadRow label="צפיות בדוחות של אחרים (30 יום)">{clinic.treatmentViews30d ?? "?"}</ReadRow>
      </div>
    </section>
  );
}

export function SystemCard({ clinic, onCopyId }) {
  const storageLimit = clinic.effectiveLimits?.storageMb;
  return (
    <section className="ncd2-card ncd2-readonly">
      <header className="ncd2-card-head">
        <span className="ncd2-card-icon is-muted">
          <LockSimple size={15} weight="bold" />
        </span>
        <div className="ncd2-card-titles">
          <span className="ncd2-card-eyebrow">קריאה בלבד</span>
          <h3 className="ncd2-card-title">נתוני מערכת</h3>
        </div>
      </header>
      <div className="ncd2-read-grid">
        <ReadRow label="Clinic ID" mono>
          <span className="ncd2-inline">
            <code>{clinic.clinicId}</code>
            <HoverTip text="העתק">
              <button type="button" className="ncd2-icon-btn" onClick={onCopyId} aria-label="העתק Clinic ID">
                <Copy size={13} />
              </button>
            </HoverTip>
          </span>
        </ReadRow>
        <ReadRow label="תאריך הקמה">{formatDate(clinic.createdAt)}</ReadRow>
        <ReadRow label="עדכון אחרון">{formatDateTime(clinic.updatedAt)}</ReadRow>
        <ReadRow label="פעילה">
          {clinic.isActive && !clinic.inactiveByAdmin ? (
            <span className="ncd2-inline">
              <Check size={13} weight="bold" className="ncd2-ok" /> כן
            </span>
          ) : (
            `לא${clinic.inactiveByAdmin ? " (הושבתה ע״י מנהל)" : ""}`
          )}
        </ReadRow>
        <ReadRow label="קליניקה יחידה (סולו)">{yesNo(clinic.isSoloClinic)}</ReadRow>
        <ReadRow label="אינטגרציית מורנינג">
          {clinic.isMorningIntegrated
            ? `מחובר${clinic.morningExempt ? " · עוסק פטור" : clinic.morningBusinessType ? " · עוסק מורשה/חברה" : ""}${
                clinic.morningDefaultDocumentType
                  ? ` · ${
                      { 305: "חשבונית מס", 320: "חשבונית מס/קבלה", 400: "קבלה", 330: "חשבונית זיכוי" }[
                        clinic.morningDefaultDocumentType
                      ] || clinic.morningDefaultDocumentType
                    }`
                  : ""
              }${clinic.morningSettingsVersion < 2 ? " · הגדרות ישנות (טרם הוסבו)" : ""}`
            : "לא מחובר"}
        </ReadRow>
        <ReadRow label="Grow תשלומים">{clinic.isGrowIntegrated ? `מחובר${clinic.growEnvironment === "sandbox" ? " (sandbox)" : ""}` : "לא מחובר"}</ReadRow>
        <ReadRow label="מס' עוסק / ח.פ.">{clinic.businessId || "—"}</ReadRow>
        <ReadRow label="כתובת">{clinic.clinicAddress || "—"}</ReadRow>
        <ReadRow label='סוג מע"מ'>{clinic.vatType || "—"}</ReadRow>
        <ReadRow label="התמחויות">{clinic.specializations?.length > 0 ? clinic.specializations.join(", ") : "—"}</ReadRow>
        <ReadRow label="טווח גילאים">{clinic.ageRange?.length > 0 ? clinic.ageRange.join(", ") : "—"}</ReadRow>
        <ReadRow label="אחסון">
          {(clinic.usedStorageInMb || 0).toLocaleString("he-IL", { maximumFractionDigits: 1 })} / {storageLimit?.toLocaleString("he-IL")} MB
        </ReadRow>
        <ReadRow label="חדרים פעילים">{clinic.roomsCount ?? "—"}</ReadRow>
        {clinic.plan === "trial" && (
          <>
            <ReadRow label="תחילת ניסיון">{formatDate(clinic.trialStartDate)}</ReadRow>
            <ReadRow label="סיום ניסיון">{formatDate(clinic.trialEndDate)}</ReadRow>
            <ReadRow label="פעולות כתיבה בניסיון">
              {clinic.trialWriteOps || 0} / {clinic.effectiveLimits?.maxWriteOpsPerTrial || 3000}
            </ReadRow>
          </>
        )}
        {clinic.planRevertedAt && <ReadRow label="חזרה לניסיון">{formatDate(clinic.planRevertedAt)}</ReadRow>}
      </div>
    </section>
  );
}
